// @ts-nocheck
import { Card, Code, Divider, Group, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { EntityList, EntityForm, EntityDetail, EntityCard } from '@/components/advanced/entity';

export default function EntityPage() {
  // --- EntityList mock data ---
  const listData = [
    { id: '1', name: 'Acme Corporation', industry: 'Manufacturing', status: 'Active', contacts: 12 },
    { id: '2', name: 'Globex Inc.', industry: 'Technology', status: 'Active', contacts: 8 },
    { id: '3', name: 'Initech LLC', industry: 'Consulting', status: 'Inactive', contacts: 3 },
    { id: '4', name: 'Umbrella Group', industry: 'Pharmaceutical', status: 'Active', contacts: 21 },
  ];

  const listColumns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'industry', label: 'Industry', sortable: true },
    {
      key: 'status',
      label: 'Status',
      render: (item) => (
        <Text size="sm" c={item.status === 'Active' ? 'green' : 'gray'} fw={500}>
          {item.status}
        </Text>
      ),
    },
    { key: 'contacts', label: 'Contacts', align: 'right' },
  ];

  const listActions = [
    { key: 'view', label: 'View', onClick: () => {} },
    { key: 'edit', label: 'Edit', onClick: () => {} },
    { key: 'delete', label: 'Delete', color: 'red', onClick: () => {} },
  ];

  // --- EntityForm mock fields ---
  const formFields = [
    { key: 'name', label: 'Company Name', type: 'text', required: true, placeholder: 'Acme Corporation' },
    { key: 'email', label: 'Email', type: 'email', placeholder: 'contact@acme.com' },
    {
      key: 'industry',
      label: 'Industry',
      type: 'select',
      options: [
        { value: 'tech', label: 'Technology' },
        { value: 'manufacturing', label: 'Manufacturing' },
        { value: 'consulting', label: 'Consulting' },
      ],
    },
    { key: 'employees', label: 'Employees', type: 'number', min: 0 },
    { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Additional details...' },
    { key: 'active', label: 'Active account', type: 'switch' },
  ];

  // --- EntityDetail mock data ---
  const detailData = {
    name: 'Acme Corporation',
    industry: 'Manufacturing',
    email: 'contact@acme.com',
    phone: '+1 555 0100',
    website: 'https://acme.example.com',
    revenue: 1250000,
    founded: '2008-04-15',
  };

  const detailSections = [
    {
      key: 'general',
      title: 'General Information',
      fields: [
        { key: 'name', label: 'Company Name' },
        { key: 'industry', label: 'Industry', type: 'badge', badgeColor: 'blue' },
        { key: 'founded', label: 'Founded', type: 'date' },
        { key: 'revenue', label: 'Annual Revenue', type: 'currency' },
      ],
    },
    {
      key: 'contact',
      title: 'Contact',
      fields: [
        { key: 'email', label: 'Email', type: 'email' },
        { key: 'phone', label: 'Phone', type: 'phone' },
        { key: 'website', label: 'Website', type: 'link' },
      ],
    },
  ];

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Entity Management</Title>
        <Text c="dimmed" size="sm">
          Reusable building blocks for CRUD experiences over any data entity: list, create/edit form,
          detail view, and card display.
        </Text>
      </Stack>

      {/* EntityList */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>EntityList</Title>
            <Text size="sm" c="dimmed">
              Data table with sorting, search, filters, column visibility, row actions, and pagination
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <EntityList
              data={listData}
              columns={listColumns}
              actions={listActions}
              title="Companies"
              description="Manage your organisation's accounts"
              onAdd={() => {}}
              onRefresh={() => {}}
              search={{ value: '', onChange: () => {}, placeholder: 'Search companies...' }}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const data = [
  { id: '1', name: 'Acme Corporation', industry: 'Manufacturing', status: 'Active', contacts: 12 },
  // ... more rows
];

const columns = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'industry', label: 'Industry', sortable: true },
  {
    key: 'status',
    label: 'Status',
    render: (item) => <Text c={item.status === 'Active' ? 'green' : 'gray'}>{item.status}</Text>,
  },
  { key: 'contacts', label: 'Contacts', align: 'right' },
];

const actions = [
  { key: 'view', label: 'View', onClick: (item) => {} },
  { key: 'edit', label: 'Edit', onClick: (item) => {} },
  { key: 'delete', label: 'Delete', color: 'red', onClick: (item) => {} },
];

<EntityList
  data={data}
  columns={columns}
  actions={actions}
  title="Companies"
  onAdd={() => {}}
  search={{ value: query, onChange: setQuery, placeholder: 'Search...' }}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • data - Array of items (each needs an id)
              <br />
              • columns - Column definitions with optional render/sortable/align
              <br />
              • actions - Per-row action menu items
              <br />
              • selectable / selectedIds / onSelectionChange - Row selection + bulkActions
              <br />
              • search / sorting / pagination / filters - Controlled toolbar features
              <br />
              • viewMode - 'table' | 'grid' | 'list'
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* EntityForm */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>EntityForm</Title>
            <Text size="sm" c="dimmed">
              Schema-driven form that renders the right input per field type with built-in validation
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <EntityForm
              title="Create Company"
              description="Fill in the details below"
              fields={formFields}
              columns={2}
              onSubmit={() => {}}
              onCancel={() => {}}
              submitLabel="Create"
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const fields = [
  { key: 'name', label: 'Company Name', type: 'text', required: true },
  { key: 'email', label: 'Email', type: 'email' },
  {
    key: 'industry',
    label: 'Industry',
    type: 'select',
    options: [
      { value: 'tech', label: 'Technology' },
      { value: 'manufacturing', label: 'Manufacturing' },
    ],
  },
  { key: 'employees', label: 'Employees', type: 'number', min: 0 },
  { key: 'notes', label: 'Notes', type: 'textarea' },
  { key: 'active', label: 'Active account', type: 'switch' },
];

<EntityForm
  title="Create Company"
  fields={fields}
  columns={2}
  onSubmit={(values) => save(values)}
  onCancel={() => close()}
  submitLabel="Create"
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Field Types:</Text>
            <Text size="xs" c="dimmed" component="div">
              text, textarea, number, email, password, phone, url, select, multiselect, checkbox,
              switch, radio, date, dateRange, file, custom
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* EntityDetail */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>EntityDetail</Title>
            <Text size="sm" c="dimmed">
              Read-only record view organised into sections with typed field formatting
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <EntityDetail
              data={detailData}
              title={(d) => d.name}
              subtitle="Customer account"
              status={{ value: 'Active', color: 'green' }}
              sections={detailSections}
              actions={{ onEdit: () => {}, onDelete: () => {}, onBack: () => {} }}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const data = {
  name: 'Acme Corporation',
  industry: 'Manufacturing',
  email: 'contact@acme.com',
  phone: '+1 555 0100',
  website: 'https://acme.example.com',
  revenue: 1250000,
  founded: '2008-04-15',
};

const sections = [
  {
    key: 'general',
    title: 'General Information',
    fields: [
      { key: 'name', label: 'Company Name' },
      { key: 'industry', label: 'Industry', type: 'badge', badgeColor: 'blue' },
      { key: 'founded', label: 'Founded', type: 'date' },
      { key: 'revenue', label: 'Annual Revenue', type: 'currency' },
    ],
  },
  {
    key: 'contact',
    title: 'Contact',
    fields: [
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'phone', label: 'Phone', type: 'phone' },
      { key: 'website', label: 'Website', type: 'link' },
    ],
  },
];

<EntityDetail
  data={data}
  title={(d) => d.name}
  subtitle="Customer account"
  status={{ value: 'Active', color: 'green' }}
  sections={sections}
  actions={{ onEdit: () => {}, onDelete: () => {}, onBack: () => {} }}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Field Types:</Text>
            <Text size="xs" c="dimmed" component="div">
              text, badge, date, currency, link, email, phone, custom (via render). Tabs can be
              supplied instead of sections.
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* EntityCard */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>EntityCard</Title>
            <Text size="sm" c="dimmed">
              Compact card representation of a single entity, available in default, compact, and
              horizontal variants
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
              <EntityCard
                title="Acme Corporation"
                subtitle="Manufacturing"
                description="Long-standing manufacturing partner with multiple active contracts."
                status={{ label: 'Active', color: 'green' }}
                badges={[{ label: 'Enterprise', color: 'blue' }, { label: 'Priority' }]}
                progress={{ value: 72, label: 'Onboarding' }}
                metadata={[
                  { label: 'Contacts', value: 12 },
                  { label: 'Deals', value: 4 },
                ]}
                actions={{ onView: () => {}, onEdit: () => {}, onDelete: () => {} }}
                onClick={() => {}}
              />
              <EntityCard
                variant="compact"
                title="Globex Inc."
                subtitle="Technology"
                avatarName="Globex"
                status={{ label: 'Active', color: 'green' }}
                actions={{ onView: () => {}, onEdit: () => {} }}
              />
              <EntityCard
                variant="horizontal"
                title="Initech LLC"
                subtitle="Consulting"
                avatarName="Initech"
                status={{ label: 'Inactive', color: 'gray' }}
                metadata={[
                  { label: 'Contacts', value: 3 },
                  { label: 'Open', value: 1 },
                ]}
                actions={{ onView: () => {} }}
              />
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<EntityCard
  title="Acme Corporation"
  subtitle="Manufacturing"
  description="Long-standing manufacturing partner."
  status={{ label: 'Active', color: 'green' }}
  badges={[{ label: 'Enterprise', color: 'blue' }, { label: 'Priority' }]}
  progress={{ value: 72, label: 'Onboarding' }}
  metadata={[
    { label: 'Contacts', value: 12 },
    { label: 'Deals', value: 4 },
  ]}
  actions={{ onView: () => {}, onEdit: () => {}, onDelete: () => {} }}
  onClick={() => {}}
/>

// Other variants
<EntityCard variant="compact" title="Globex Inc." subtitle="Technology" />
<EntityCard variant="horizontal" title="Initech LLC" subtitle="Consulting" />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • title / subtitle / description / image / avatar
              <br />
              • status / badges / progress / metadata
              <br />
              • actions - onView / onEdit / onDelete / custom
              <br />
              • variant - 'default' | 'compact' | 'horizontal'
              <br />
              • selectable / selected / onSelect
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>
          <Divider />

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>List + Detail flow</Text>
              <Text size="xs" c="dimmed">
                Pair EntityList with EntityDetail to build a master-detail screen: click a row to open
                the full record without leaving the page.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Create &amp; Edit</Text>
              <Text size="xs" c="dimmed">
                Drive both creation and editing from a single EntityForm by passing initialValues and
                swapping the submitLabel.
              </Text>
            </Stack>

            <Group gap="xs" align="flex-start">
              <Stack gap="xs">
                <Text size="sm" fw={500}>Card galleries</Text>
                <Text size="xs" c="dimmed">
                  Use EntityCard inside a SimpleGrid for dashboards and directory views where a visual
                  summary beats a dense table.
                </Text>
              </Stack>
            </Group>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Bulk operations</Text>
              <Text size="xs" c="dimmed">
                Enable selectable on EntityList and supply bulkActions to let users archive, export, or
                delete many records at once.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
